import React from "react";
import { Box, Typography, Chip, Tooltip } from "@mui/material";

// One renderer for a library (v2) test cell, shared by the Library Tests matrix and the Practice
// Exams matrix (which folds in the tests flagged show_in_exam_tab). Both read the same cell shape
// from buildV2TestCells, so a new status or a scoring change lands in both tabs at once — keeping
// them as two hand-synced copies is how they silently drift apart.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// Score shown in a "taken" cell: scaled (full SAT/ACT) → composite → raw MCQ (vocab & MCQ-only).
// v2 sessions carry no section breakdown, so these cells show the number alone.
// The SAME pick order is implemented server-side as prepV2Score (helpers/prep_score.js), for the
// generated report PDF — a server-rendered page can't import this module. Change one, change both.
export const v2Score = (cell) => {
    if (cell.scaled_score != null) return `${cell.scaled_score}`;
    if (cell.composite_score != null) return `${cell.composite_score}`;
    if (cell.mcq_score != null) return `${cell.mcq_score} / ${cell.mcq_total ?? "–"}`;
    return "✓";
};

// A dangling adopted link is permanent and needs a different fix than a transient load failure, so
// it gets its own glyph rather than reusing the exam matrix's ⚠.
export const V2_UNLINKED_GLYPH = "⊘";

// `test` supplies the drill-down target (course.uuid + v2_course_test_id); the cell supplies session_id.
export const renderV2Cell = (cell, test) => {
    if (!cell) return <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>;
    switch (cell.status) {
        case "taken": {
            const late = cell.on_time === false;
            const incomplete = !!cell.incomplete;
            const clickable = !!(cell.session_id && test?.v2_course_test_id && test?.course?.uuid);
            const multi = cell.attempts > 1;
            // Component-scored tests (ACT) carry a section breakdown and the alternate composites
            // — e.g. the four-way average including Science, which the headline deliberately omits.
            const alts = Object.entries(cell.alternates || {})
                .filter(([name, v]) => name !== "composite" && typeof v === "number")
                .map(([name, v]) => `${name.replace(/_/g, " ")} ${v}`);
            const tip = [
                incomplete ? "submitted — grading pending" : v2Score(cell),
                cell.detail || null,
                ...alts,
                multi ? `best of ${cell.attempts} attempts` : null,
                cell.end_time ? fmtDate(cell.end_time) : null,
                late ? "late" : null,
                clickable ? "click for result" : null,
            ].filter(Boolean).join(" · ");
            return (
                <Tooltip title={tip} arrow>
                    <Typography
                        variant="body2"
                        onClick={clickable
                            ? () => window.open(`/classrooms/${test.course.uuid}/test-v2/${test.v2_course_test_id}/result/${cell.session_id}`, "_blank")
                            : undefined}
                        sx={{
                            color: incomplete ? "info.main" : late ? "warning.main" : "success.main",
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            cursor: clickable ? "pointer" : "default",
                            textDecoration: clickable ? "underline dotted" : "none",
                            "&:hover": clickable ? { textDecoration: "underline" } : {},
                        }}
                    >
                        {incomplete ? "✓ pending" : (
                            <>
                                {v2Score(cell)}{late ? " *" : ""}
                                {/* Section detail inline, so an ACT library row reads like the ETT
                                    exam rows beside it rather than as a bare number. */}
                                {cell.detail ? (
                                    <Box component="span" sx={{ fontWeight: 400, opacity: 0.75 }}> ({cell.detail})</Box>
                                ) : null}
                                {multi ? <Box component="span" sx={{ fontWeight: 400, opacity: 0.6 }}> ×{cell.attempts}</Box> : ""}
                            </>
                        )}
                    </Typography>
                </Tooltip>
            );
        }
        case "in_progress":
            return <Chip size="small" color="info" variant="outlined" label="In progress" />;
        case "not_started":
            return <Typography variant="caption" sx={{ color: "text.disabled" }}>Not started</Typography>;
        case "scheduled":
            return <Tooltip title="Not released yet" arrow><Typography variant="caption" sx={{ color: "text.disabled" }}>Scheduled</Typography></Tooltip>;
        case "unlinked":
            return (
                <Tooltip title="The course assignment this was adopted from has been deleted — remove this test from the program" arrow>
                    <Typography variant="caption" sx={{ color: "warning.main" }}>{V2_UNLINKED_GLYPH}</Typography>
                </Tooltip>
            );
        case "not_assigned":
            return <Tooltip title="Not assigned to this student" arrow><Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography></Tooltip>;
        default:
            return <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>;
    }
};

// Legend fragment for the statuses above — kept beside the renderer so a new status can't be added
// to one without the other being obviously stale.
export const V2_CELL_LEGEND =
    `In progress · Not started · Scheduled (unreleased) · — not assigned · ${V2_UNLINKED_GLYPH} adopted assignment deleted`;
